import type { UserSociety } from "@/services/api/society/getUserSocieties";
import type {
  EventContactPair,
  EventFormPayload,
  StudentEvent,
} from "@/services/api/user/events";
import {
  toArray,
  toDateKey,
  toHashTags,
  toImageList,
  toUniversityCodes,
} from "@/utils/eventFormat";

/** Blank wizard state: open to everyone, no repeat, all-day off. */
export const emptyEventForm = (): EventFormPayload => ({
  societyIds: [],
  universityCodes: [],
  imageUrls: [],
  title: "",
  description: "",
  location: "",
  phoneList: [],
  emailList: [],
  linkList: [],
  tagList: [],
  startDate: "",
  endDate: "",
  allDay: false,
  startTime: "",
  endTime: "",
  neverRepeat: true,
  dailyRepeat: false,
  weeklyRepeat: false,
  endDateOn: "",
  deadlineDate: "",
  deadlineTime: "",
  allowJoin: false,
});

/** Read the society ids out of the loosely-typed `society_id` JSON column. */
const toSocietyIds = (value: unknown): string[] =>
  toArray(value)
    .map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        return String((item as { id?: unknown }).id ?? "");
      }
      return "";
    })
    .filter(Boolean);

/** Read the `phones` / `emails` / `links` columns into editable label/value pairs. */
const toContactPairs = (value: unknown): EventContactPair[] =>
  toArray(value)
    .map((item) => {
      if (item && typeof item === "object") {
        const pair = item as { input1?: unknown; input2?: unknown };
        return { input1: String(pair.input1 ?? ""), input2: String(pair.input2 ?? "") };
      }
      return { input1: "", input2: String(item ?? "") };
    })
    .filter((pair) => pair.input2.length > 0);

/**
 * Turn a stored event back into wizard state so the edit form opens
 * pre-filled. `societies` is the organizer's own society list, used to keep
 * the `university` payload in sync with the selected societies the same way
 * the society step does.
 */
export const toEventFormPayload = (
  event: StudentEvent,
  societies: UserSociety[],
): EventFormPayload => {
  const details = event.details;
  const societyIds = toSocietyIds(details.society_id);
  const universityCodes = Array.from(
    new Set(
      societyIds
        .map((id) => societies.find((society) => society.id === id)?.university ?? null)
        .filter((code): code is string => Boolean(code)),
    ),
  );

  const repeatType = details.repeat_type ?? null;
  const neverRepeat =
    repeatType !== null
      ? repeatType === "never"
      : !details.daily_repeat && !details.weekly_repeat;

  return {
    ...emptyEventForm(),
    societyIds,
    universityCodes: universityCodes.length > 0
      ? universityCodes
      : toUniversityCodes(details.university),
    imageUrls: toImageList(details.event_images),
    title: details.title ?? "",
    description: details.description ?? "",
    location: details.location ?? "",
    phoneList: toContactPairs(details.phones),
    emailList: toContactPairs(details.emails),
    linkList: toContactPairs(details.links),
    tagList: toHashTags(details.hash_tags),
    startDate: toDateKey(details.start_date) ?? "",
    endDate: toDateKey(details.end_date) ?? "",
    allDay: details.all_day === 0,
    startTime: details.start_time ?? "",
    endTime: details.end_time ?? "",
    neverRepeat,
    dailyRepeat: repeatType ? repeatType === "daily" : details.daily_repeat === 1,
    weeklyRepeat: repeatType
      ? repeatType === "weekly"
      : details.weekly_repeat === 1,
    endDateOn: toDateKey(details.end_date_on) ?? "",
    deadlineDate: toDateKey(details.deadline_date) ?? "",
    deadlineTime: details.deadline_time ?? "",
    allowJoin: details.allow_join_event !== false,
  };
};
